/**
 * useGuidedProgress — wylicza postęp usera w APISAI Guided Workspace.
 *
 * Zwraca:
 *   { hasProfile, hasHero, hasPosts, hasLeads, loading }
 *
 * Logika:
 *   - hasProfile = istnieje kennel/business o status=published lub draft
 *   - hasHero    = istnieje hero_config o status=published
 *   - hasPosts   = istnieje >= 1 post w /api/posts/mine (TODO when endpoint exists)
 *   - hasLeads   = istnieje >= 1 lead w /api/leads/mine
 *
 * Cache: 60s w sessionStorage żeby uniknąć ping/floodu.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

const CACHE_KEY = 'apisai_guided_progress';
const CACHE_TTL_MS = 60 * 1000;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function readCache(userId) {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj.userId !== userId) return null;
    if (Date.now() - obj.ts > CACHE_TTL_MS) return null;
    return obj.data;
  } catch { return null; }
}

function writeCache(userId, data) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ userId, ts: Date.now(), data }));
  } catch {}
}

export function useGuidedProgress() {
  const { user } = useAuth() || {};
  const [progress, setProgress] = useState({ hasProfile: false, hasHero: false, hasPosts: false, hasLeads: false, primaryBreed: null, loading: true });

  useEffect(() => {
    if (!user?.id) {
      setProgress((p) => ({ ...p, loading: false }));
      return;
    }
    const cached = readCache(user.id);
    if (cached) {
      setProgress({ ...cached, loading: false });
      return;
    }

    let cancelled = false;
    (async () => {
      const headers = getAuthHeaders();
      const isBiznes = user?.profile_type === 'biznes';

      // Profile check (also extract primary breed for breed-aware UI)
      let hasProfile = false;
      let primaryBreed = null;
      let firstEntityId = null;
      try {
        const url = isBiznes ? `${API}/business/my` : `${API}/kennels`;
        const res = await axios.get(url, { headers });
        const items = res?.data?.items || res?.data || [];
        hasProfile = Array.isArray(items) ? items.length > 0 : Boolean(items?.id || items?.slug);
        firstEntityId = Array.isArray(items) ? items[0]?.id || null : items?.id || null;
        // Extract first concrete breed (drives breedContext across the app).
        // Priority: first kennel with a specific breed (skip "Mieszany"/empty).
        if (!isBiznes && Array.isArray(items)) {
          const concrete = items.find((k) => {
            const b = k?.breeds?.[0];
            return b && b.trim().length > 0 && b.toLowerCase() !== 'mieszany';
          });
          primaryBreed = concrete?.breeds?.[0] || items[0]?.breeds?.[0] || null;
        }
      } catch {}

      // Hero check (istniejący endpoint /hero-creator/draft; wymaga entity)
      let hasHero = false;
      if (firstEntityId) {
        try {
          const entityType = isBiznes ? 'business' : 'kennel';
          const res = await axios.get(`${API}/hero-creator/draft`, {
            headers,
            params: { entity_type: entityType, entity_id: firstEntityId },
          });
          const cfg = res?.data?.config;
          hasHero = !!(cfg && cfg.status === 'published' && cfg.headline);
        } catch {}
      }

      // Leads check (ten sam endpoint co Centrum dowodzenia)
      let hasLeads = false;
      try {
        const res = await axios.get(`${API}/panel/leads/all`, { headers });
        const items = res?.data?.items || res?.data || [];
        hasLeads = Array.isArray(items) && items.length > 0;
      } catch {}

      // Posts: brak endpointu listującego posty usera — flaga pozostaje false
      // (wcześniejsze wywołanie /posts/mine zawsze kończyło się 404 i spamowało konsolę).
      const hasPosts = false;

      const data = { hasProfile, hasHero, hasPosts, hasLeads, primaryBreed };
      if (!cancelled) {
        writeCache(user.id, data);
        setProgress({ ...data, loading: false });
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id, user?.profile_type]);

  return progress;
}
